import { useState, type FormEvent } from "react"
import { ArrowLeft, Mail, Loader2 } from "lucide-react"
import { Footer } from "@/components/Footer"
import { getEventSlugFromUrl, eventPath } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

const WEBHOOK_URL = "https://n8n.codeblocks.sk/webhook/contact-form"
const SUPPORT_EMAIL = "info@turnago.com"

export default function Support() {
  const slug = getEventSlugFromUrl()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)

    try {
      await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      })
      setSubmitted(true)
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-3xl px-4 py-10 @min-[40rem]:py-16">
        <a
          href={eventPath(slug)}
          className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back
        </a>

        <h1 className="text-3xl font-bold tracking-tight text-foreground @min-[40rem]:text-4xl">
          Support
        </h1>
        <p className="mt-2 text-base text-muted-foreground">
          Need help? Get in touch with our team.
        </p>

        <div className="mt-8 rounded-xl border border-border bg-card/60 p-6 @min-[40rem]:p-8">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mb-8 flex items-center gap-3 rounded-lg px-4 py-3 transition-colors hover:bg-muted/50"
          >
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Mail className="size-4 text-primary" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-semibold text-foreground">Email us directly</span>
              <span className="text-sm text-muted-foreground">{SUPPORT_EMAIL}</span>
            </div>
          </a>

          <div className="mb-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">or use the form below</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          {submitted ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
                <Mail className="size-5 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Message sent</h2>
              <p className="text-sm text-muted-foreground">
                We&apos;ll get back to you as soon as possible.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground">
                  Name
                </label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-sm font-medium text-foreground">
                  Message
                </label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  className="flex min-h-[120px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-xs outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <Button type="submit" disabled={submitting} size="xl" className="mt-2 w-full">
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Message"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>

      <Footer eventSlug={slug} />
    </div>
  )
}
